import { useSyncExternalStore } from "react";
import { formatDate, translate } from "@/i18n";
import { desktopBrowserMessages } from "@/i18n/messages/desktop-browser";
import type { BrowserConnectionStatus, BrowserId } from "./browser-connection";
import {
  getDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
} from "./desktop-chat-auth-scope";

const PREFIX = "denker.browser_connections.verified.v1:";
type History = Partial<Record<BrowserId, number>>;
const memory = new Map<string, History>();
const failedWrites = new Set<string>();
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());
subscribeDesktopChatAuthScope(notify);

function key(): string | null {
  const account = getDesktopChatAuthScope()?.accountId;
  return account ? `${PREFIX}${encodeURIComponent(account)}` : null;
}

function read(storageKey: string): History {
  if (failedWrites.has(storageKey)) return memory.get(storageKey) ?? {};
  try {
    const value: unknown = JSON.parse(localStorage.getItem(storageKey) ?? "{}");
    return value && typeof value === "object" && !Array.isArray(value)
      ? value
      : {};
  } catch {
    return memory.get(storageKey) ?? {};
  }
}

export function getBrowserLastVerified(browserId: BrowserId): number | null {
  const storageKey = key();
  if (!storageKey) return null;
  const history = read(storageKey);
  const valid = (value: unknown): value is number =>
    typeof value === "number" &&
    Number.isFinite(value) &&
    value > 0 &&
    value <= Date.now();
  const time = history[browserId];
  return valid(time) ? time : null;
}

/** Temporary unavailability leaves the last successful verification intact. */
export function browserConnectionNeedsSetup(
  status: BrowserConnectionStatus,
): boolean {
  return (
    !status.installed ||
    ["consent_required", "denied"].includes(status.automation.status) ||
    ["javascript_disabled", "launch_flag_required", "disconnected"].includes(
      status.reason,
    )
  );
}

/** Only fresh native evidence can establish or revoke a saved connection. */
export function recordBrowserConnectionObservation(
  status: BrowserConnectionStatus,
): void {
  const storageKey = key();
  if (!storageKey) return;
  const ready =
    status.installed &&
    status.automation.status === "granted" &&
    status.javascript === "ready" &&
    status.reason === "ready";
  if (!ready && !browserConnectionNeedsSetup(status)) return;
  const history = { ...read(storageKey) };
  if (ready) history[status.browser_id] = Date.now();
  else delete history[status.browser_id];
  write(storageKey, history);
}

function write(storageKey: string, history: History): void {
  memory.set(storageKey, history);
  try {
    localStorage.setItem(storageKey, JSON.stringify(history));
    failedWrites.delete(storageKey);
  } catch {
    failedWrites.add(storageKey);
    // Keep verification available in this webview when storage is unavailable.
  }
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const changed = (event: StorageEvent) => {
    if (event.key === null || event.key === key()) {
      if (event.key) failedWrites.delete(event.key);
      else failedWrites.clear();
      listener();
    }
  };
  window.addEventListener("storage", changed);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", changed);
  };
}

export function useBrowserLastVerified(browserId: BrowserId): number | null {
  return useSyncExternalStore(
    subscribe,
    () => getBrowserLastVerified(browserId),
    () => null,
  );
}

export function browserLastVerifiedLabel(time: number): string {
  return translate(desktopBrowserMessages, "lastVerified", {
    time: formatDate(time, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
  });
}
